import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import { Button, TextField, Typography, Card, CardContent, Grid } from "@mui/material";
import Isabelle from "./../../assets/images/isabelle.svg";
import CloseIcon from "mdi-material-ui/Close";
import SendIcon from "mdi-material-ui/Send";
import CustomFab from "./layout/CustomFab";
import { CustomMessages, TMessage } from "./layout/CustomMessages";
import TypingMessage from "./layout/TypingMessage";
import {
  chatBotContainer,
  ChatBotTextCOntainer,
  ChatPrimaryText,
  ChatSecondaryText,
  closeButtonDesign,
  discussionHeaderContainer,
  discussionHeaderContainerMobile,
  discussionInterfaceContainer,
  discussionInterfaceContainerMobile,
  floatingButtonDesign,
  floatingMessageDesign,
  IconContainer,
  IconImageDesign,
  MessagesContainerDesign,
  notifMessageDesign,
  notifTextDesign,
} from "../../styles/ChatBot/ChatBot.style";
import {
  sendMessageContainer,
  sendMessageInputDesign,
  typingInnerMessageContainer,
} from "../../styles/ChatBot/CustomMessages.style";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  getChatBotResponse,
  getFAQuestions,
} from "../../common/data/chatBotAPI";
import { useTranslation } from "react-i18next";
import Suggestions from "./layout/Suggestions";
import { useDispatch } from "react-redux";
import { newTicket, newChatBotTicket } from "../../redux/reclamation/reclamation";
import LoginDialog from "../../components/modals/loginModal";

// Types for claim submission
interface TicketCategory {
  id: string;
  name: Record<string, string>;
  serviceId: string | null;
}

interface ParkingSite {
  id: string;
  parkings: Parking[];
  siteName: Record<string, string>;
}

interface Parking {
  id: string;
  parkingName: Record<string, string>;
}

// Helper to escape HTML characters
const escapeHtml = (text: string) => {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const ChatBot = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const isLessThanSm = useMediaQuery(theme.breakpoints.down("sm"));
  const isFirstInteraction = localStorage.getItem("isFirstInteraction") || "0";
  const [shake, setShake] = useState<boolean>(false);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [recievedMessagesNotif, setRecievedMessagesNotif] =
    useState<boolean>(false);
  const [openDiscussion, setOpenDiscussion] = useState<boolean>(false);
  const [messages, setMessages] = useState<TMessage[]>([]);
  const [messageInput, setMessageInput] = useState("");
  const [messageToDisplay, setMessageToDisplay] = useState<string | null>(null);
  const [pending, setPending] = useState<boolean>(false);
  const [fAskedQuestions, setFAskedQuestions] = useState<string[]>([
    "Quel prix pour 1h pour  le parking Médiacité ?",
    "J'habite Rixensart, où puis-je trouver un parking ?",
    "Quel est le prix pour reservation de 1h a  grand place Louvain la neuve?",
    "Comment puis-je résilier mon abonnement ?",
  ]);
  const [displayFAQ, setDisplayFAQ] = useState<boolean>(false);
  const [openLoginDialog, setOpenLoginDialog] = useState<boolean>(false);

  // Handle login dialog
  const handleCloseLoginDialog = () => {
    setOpenLoginDialog(false);
  };

  // Claim submission states
  const [isClaimSubmissionMode, setIsClaimSubmissionMode] =
    useState<boolean>(false);
  const [ticketCategories, setTicketCategories] = useState<TicketCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<TicketCategory | null>(null);
  const [parkingSites, setParkingSites] = useState<ParkingSite[]>([]);
  const [selectedSite, setSelectedSite] = useState<ParkingSite | null>(null);
  const [selectedParking, setSelectedParking] = useState<Parking | null>(null);
  const [reclamationText, setReclamationText] = useState<string>("");
  const [claimStep, setClaimStep] = useState<
    "categories" | "sites" | "parkings" | "description" | "confirmation"
  >("categories");
  const [showFooter, setShowFooter] = useState<boolean>(true);
  const [showConfirmationButtons, setShowConfirmationButtons] = useState<boolean>(false);

  const handleClickMessage = (question: string) => {
    setDisplayFAQ(false);
    handleSendMessage(question, "me");
  };

  // Helper function to get locale-specific name
  const getLocalizedName = (nameObject: Record<string, string>): string => {
    const currentLang = i18n.language.toUpperCase();
    return (
      nameObject[currentLang] ||
      nameObject["FR"] ||
      Object.values(nameObject)[0] ||
      "N/A"
    );
  };

  // Handle claim submission initiation
  const handleStartClaimSubmission = (
    sites: ParkingSite[],
    categories: TicketCategory[]
  ) => {
    // Add "Autre" option to sites
    const sitesWithOther = [
      ...sites,
      {
        id: "other_site",
        siteName: { FR: "Autre", EN: "Other", NL: "Andere" },
        parkings: [],
      },
    ];

    setParkingSites(sitesWithOther);

    // Sort categories to make sure "Other" is at the end
    const otherCategoryIndex = categories.findIndex((c) =>
      Object.values(c.name).some((val) =>
        ["other", "autre", "andere"].includes(val.toLowerCase())
      )
    );

    let sortedCategories = categories;

    if (otherCategoryIndex !== -1) {
      const otherCategory = categories[otherCategoryIndex];
      const otherCategories = categories.filter(
        (_, index) => index !== otherCategoryIndex
      );
      sortedCategories = [...otherCategories, otherCategory];
    }

    setTicketCategories(sortedCategories);
    setIsClaimSubmissionMode(true);
    setClaimStep("categories");
    setShowFooter(false); // Hide footer during selection
    setPending(false);

    // Add local chatbot message for category selection
    setMessages((messages) => [
      ...messages,
      {
        text: t("Veuillez sélectionner la catégorie de votre réclamation"),
        user: "other",
        data: { type: "categorySelection" },
      },
    ]);
  };

  // Handle category selection
  const handleSelectCategory = (category: TicketCategory) => {
    setSelectedCategory(category);

    // Only add site selection message if it doesn't already exist
    setMessages((messages) => {
      const hasSiteMessage = messages.some(
        (m) => m.data?.type === "siteSelection"
      );
      if (hasSiteMessage) {
        return messages; // Don't add duplicate message
      }
      return [
        ...messages,
        {
          text: t("Veuillez sélectionner la ville de votre réclamation"),
          user: "other",
          data: { type: "siteSelection" },
        },
      ];
    });

    // Only change to "sites" step if we're currently at "categories"
    // This preserves the parking list if user is re-selecting category
    if (claimStep === "categories") {
      setClaimStep("sites");
    }
  };

  // Handle site selection
  const handleSelectSite = (site: ParkingSite) => {
    // Check if "Autre" site is selected
    if (site.id === "other_site") {
      setSelectedSite(site);
      setSelectedParking(null); // No parking for "Autre" site
      setClaimStep("description"); // Skip parking selection
      setShowFooter(true); // Show footer so user can type description

      // Add local chatbot message asking for description immediately
      setMessages((messages) => {
        // Remove parking selection message if present (cleanup on re-selection)
        const updatedMessages = messages.filter(
          (m) => m.data?.type !== "parkingSelection"
        );

        return [
          ...updatedMessages,
          {
            text: t("Veuillez bien décrire votre réclamation"),
            user: "other",
          },
        ];
      });
      return;
    }

    // Normal site selection logic
    // If changing site, clear parking selection
    if (selectedSite?.id !== site.id) {
      setSelectedParking(null);
    }

    // Add "Autre" option to parkings if not present
    const parkingsWithOther = site.parkings.some((p) => p.id === "Other")
      ? site.parkings
      : [
        ...site.parkings,
        {
          id: "Other",
          parkingName: { FR: "Autre", EN: "Other", NL: "Andere" },
        },
      ];

    const updatedSite = { ...site, parkings: parkingsWithOther };
    setSelectedSite(updatedSite);

    // Only add parking selection message if it doesn't already exist
    setMessages((messages) => {
      const hasParkingMessage = messages.some(
        (m) => m.data?.type === "parkingSelection"
      );
      if (hasParkingMessage) {
        return messages; // Don't add duplicate message
      }
      return [
        ...messages,
        {
          text: t("Veuillez sélectionner un parking"),
          user: "other",
          data: { type: "parkingSelection" },
        },
      ];
    });

    setClaimStep("parkings");
  };

  // Handle parking selection
  const handleSelectParking = (parking: Parking) => {
    setSelectedParking(parking);
    setShowFooter(true); // Show footer after parking is selected

    // Add local chatbot message asking for description
    setMessages((messages) => [
      ...messages,
      {
        text: t("Veuillez bien décrire votre réclamation"),
        user: "other",
      },
    ]);

    setClaimStep("description");
  };

  // Handle description submission
  const handleSubmitDescription = () => {
    if (reclamationText.trim() === "") return;

    // Add user's description as a message
    setMessages((messages) => [
      ...messages,
      {
        text: reclamationText,
        user: "me",
      },
    ]);

    // Show footer with confirmation buttons
    setShowFooter(true);
    setShowConfirmationButtons(true);

    // Add confirmation message
    let confirmationText = `${t(
      "Veuillez confirmer l'envoi de votre réclamation"
    )}:\n\n${t("Catégorie")}: <b>${getLocalizedName(selectedCategory!.name)}</b>\n${t(
      "Ville"
    )}: <b>${getLocalizedName(selectedSite!.siteName)}</b>`;

    if (selectedParking) {
      confirmationText += `\n${t("Parking")}: <b>${getLocalizedName(
        selectedParking.parkingName
      )}</b>`;
    }

    confirmationText += `\n${t("Description")}: <b>${escapeHtml(reclamationText)}</b>`;

    setMessages((messages) => [
      ...messages,
      {
        text: confirmationText,
        user: "other",
      },
    ]);

    setClaimStep("confirmation");
  };

  // Handle cancel claim submission
  const handleCancelClaim = () => {
    // Remove all reclamation-related messages and add cancellation message
    setMessages((messages) => {
      // Filter out all messages related to reclamation process
      const filteredMessages = messages.filter(
        (msg) =>
          // Keep messages that are NOT part of reclamation flow
          msg.data?.type !== "categorySelection" &&
          msg.data?.type !== "siteSelection" &&
          msg.data?.type !== "parkingSelection" &&
          // Remove description prompt message
          !msg.text.includes(t("Veuillez bien décrire votre réclamation")) &&
          // Remove confirmation message
          !msg.text.includes(
            t("Veuillez confirmer l'envoi de votre réclamation")
          ) &&
          // Remove user's description if it matches
          !(msg.user === "me" && msg.text === reclamationText)
      );

      // Add cancellation message
      return [
        ...filteredMessages,
        {
          text: t("Réclamation annulée"),
          user: "other",
        },
      ];
    });

    // Clear all claim submission states
    setIsClaimSubmissionMode(false);
    setSelectedCategory(null);
    setSelectedSite(null);
    setSelectedParking(null);
    setReclamationText("");
    setClaimStep("categories");
    setTicketCategories([]);
    setParkingSites([]);
    setMessageInput("");
    setShowFooter(true); // Restore footer
    setShowConfirmationButtons(false);
    setPending(false);
  };

  // Handle send reclamation (from confirmation)
  const handleSendReclamation = async () => {
    if (reclamationText.trim() === "") return;
    setPending(true);
    try {
      const ticketData = {
        description: reclamationText,
        category_id: selectedCategory?.id,
        parking_id: (selectedSite?.id === "other_site" || selectedParking?.id === "Other") ? null : selectedParking?.id,
        site_id: selectedSite?.id === "other_site" ? null : selectedSite?.id,
        current_language: i18n.language.toUpperCase(),
      };

      console.log("Reclamation Data:", ticketData);

      const result = await dispatch(newChatBotTicket(ticketData) as any);

      if (result.payload?.Success) {
        // Construct the detailed success message
        let successText =
          `${t("Votre réclamation")},\n\n` +
          `${t("Catégorie")}: <b>${getLocalizedName(
            selectedCategory!.name
          )}</b>\n` +
          `${t("Ville")}: <b>${getLocalizedName(selectedSite!.siteName)}</b>\n`;

        if (selectedParking) {
          successText += `${t("Parking")}: <b>${getLocalizedName(
            selectedParking.parkingName
          )}</b>\n`;
        }

        successText +=
          `${t("Description")}: <b>${escapeHtml(reclamationText)}</b>\n\n` +
          `${t(
            "a été envoyée avec succès. Puis-je vous aider avec autre chose?"
          )}`;

        // Update messages: Remove all reclamation-related messages and add the success message
        setMessages((messages) => {
          const filteredMessages = messages.filter(
            (msg) =>
              // Remove selection messages
              msg.data?.type !== "categorySelection" &&
              msg.data?.type !== "siteSelection" &&
              msg.data?.type !== "parkingSelection" &&
              // Remove description prompt
              !msg.text.includes(
                t("Veuillez bien décrire votre réclamation")
              ) &&
              // Remove confirmation prompt
              !msg.text.includes(
                t("Veuillez confirmer l'envoi de votre réclamation")
              ) &&
              // Remove user's description
              !(msg.user === "me" && msg.text === reclamationText)
          );

          return [
            ...filteredMessages,
            {
              text: successText,
              user: "other",
            },
          ];
        });
      } else {
        throw new Error("Failed to submit ticket");
      }
    } catch (error) {
      console.error("Error submitting ticket:", error);
      setMessages((messages) => {
        const filteredMessages = messages.filter(
          (msg) =>
            msg.data?.type !== "categorySelection" &&
            msg.data?.type !== "siteSelection" &&
            msg.data?.type !== "parkingSelection" &&
            !msg.text.includes(t("Veuillez bien décrire votre réclamation")) &&
            !msg.text.includes(
              t("Veuillez confirmer l'envoi de votre réclamation")
            ) &&
            !(msg.user === "me" && msg.text === reclamationText)
        );

        return [
          ...filteredMessages,
          {
            text: t(
              "Une erreur est survenue lors de l'enregistrement de votre réclamation. Veuillez réessayer."
            ),
            user: "other",
          },
        ];
      });
    } finally {
      // Clear all states
      setIsClaimSubmissionMode(false);
      setSelectedCategory(null);
      setSelectedSite(null);
      setSelectedParking(null);
      setReclamationText("");
      setClaimStep("categories");
      setTicketCategories([]);
      setParkingSites([]);
      setMessageInput("");
      setShowFooter(true); // Restore footer
      setShowConfirmationButtons(false);
      setPending(false);
    }
  };

  useEffect(() => {
    const fetchFAQs = async () => {
      const FAQs = await getFAQuestions(
        `${process.env.REACT_APP_CHATBOT}/chat`
      );
      if (FAQs) {
        setFAskedQuestions(FAQs);
      }
    };

    fetchFAQs();
  }, []);
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollTop = chatEndRef.current.scrollHeight;
    }
  }, [messages, openDiscussion, claimStep, selectedCategory, selectedSite, selectedParking]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollTop = chatEndRef.current.scrollHeight;
    }
  }, [claimStep]);

  useEffect(() => {
    if (isClaimSubmissionMode && chatEndRef.current) {
      chatEndRef.current.scrollTop = chatEndRef.current.scrollHeight;
    }
  }, [isClaimSubmissionMode]);

  useEffect(() => {
    if (messages.length === 0 && isFirstInteraction === "0") {
      setDisplayFAQ(false);
      localStorage.setItem("isFirstInteraction", "1");
      setTimeout(async () => {
        setPending(true);
        try {
          let startingMessage =
            "Bonjour! Je suis Isabel, un assistant compétent spécialisé dans les demandes de renseignements liées à la société de mobilité Effia Belgium.";
          setMessageToDisplay(t(startingMessage).slice(0, 20) + "...");
          setMessages((messages) => [
            ...messages,
            { text: t(startingMessage), user: "other" },
          ]);
        } catch {
          setMessages((messages) => [
            ...messages,
            { text: t("Service non disponible pour le moment"), user: "other" },
          ]);
        } finally {
          setPending(false);
          if (!openDiscussion) {
            handlesShake();
            handleShowMessage();
          }
        }
      }, 10000);
    }
    if (isFirstInteraction === "1") {
      setDisplayFAQ(true);
      setMessages((messages) => [
        ...messages,
        { text: "Une question? Je suis là pour vous aider!", user: "other" },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleSendMessage = async (msg: string, user: "me" | "other") => {
    if (msg.trim() === "") return;
    setPending(true);
    try {
      if (user === "me") {
        await setMessages((messages) => [
          ...messages,
          { text: msg, user: user },
        ]);
        await setMessageInput("");
        // Clean messages to only include text and user, removing any extra data properties
        let messageToSend = [
          ...messages
            .slice(Math.max(messages.length - 5, 0))
            .map((m) => ({ text: m.text, user: m.user })),
          { text: msg, user: user },
        ];
        const res = await getChatBotResponse(
          `${process.env.REACT_APP_CHATBOT}/chat`,
          messageToSend
        );
        // Check if login is required
        if (res.data.response_type === "login_required") {
          // Display the chatbot response message
          if (res.data.response) {
            const text = res.data.response;
            setMessageToDisplay(text.slice(0, 20) + "...");
          }
          setMessages((messages) => [
            ...messages,
            { text: res.data.response, user: "other" },
          ]);
          // Open login dialog
          setOpenLoginDialog(true);
          setPending(false);
        } else if (
          res.data.response_type === "claim_submission" &&
          res.data.data?.parking_sites &&
          res.data.data?.ticket_categories
        ) {
          // Don't display the empty response message, only start the claim submission flow
          handleStartClaimSubmission(
            res.data.data.parking_sites,
            res.data.data.ticket_categories
          );
        } else {
          if (res.data.response) {
            const text = res.data.response;
            setMessageToDisplay(text.slice(0, 20) + "...");
          }
          setMessages((messages) => [
            ...messages,
            { text: res.data.response, user: "other" },
          ]);
        }
      } else {
        setMessages((messages) => [...messages, { text: msg, user }]);
      }
    } catch {
      setMessages((messages) => [
        ...messages,
        { text: t("Service non disponible pour le moment"), user: "other" },
      ]);
    } finally {
      setPending(false);
      // Only show shake and message notifications if not in claim submission mode
      if (openDiscussion && !isClaimSubmissionMode) {
        handlesShake();
        handleShowMessage();
      }
    }
  };
  const handlesShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 300);
  };
  const handleShowMessage = () => {
    setShowMessage(true);
    setTimeout(() => {
      setShowMessage(false);
      setRecievedMessagesNotif(true);
      setMessageToDisplay(null);
    }, 2000);
  };
  const handleKeyDown = (e: any) => {
    if (e.key === "Enter" && !e.shiftKey && !pending) {
      e.preventDefault();
      if (isClaimSubmissionMode && claimStep === "description") {
        handleSubmitDescription();
      } else if (!isClaimSubmissionMode) {
        handleSendMessage(messageInput, "me");
      }
    }
  };
  const handleClick = () => {
    setRecievedMessagesNotif(false);
    setOpenDiscussion(!openDiscussion);
  };

  return (
    <Box sx={chatBotContainer}>
      <Box sx={floatingButtonDesign}>
        <CustomFab
          shake={shake}
          openDiscussion={openDiscussion}
          handleClick={handleClick}
        />
        {showMessage && !openDiscussion && (
          <Box sx={{ ...floatingMessageDesign }}>{messageToDisplay}</Box>
        )}
        {recievedMessagesNotif && !openDiscussion && (
          <Box sx={notifMessageDesign}>
            <Typography sx={notifTextDesign}>1</Typography>
          </Box>
        )}
      </Box>
      {pending &&
        !openDiscussion &&
        !recievedMessagesNotif &&
        !messageToDisplay && <TypingMessage sx={{ right: "-30%" }} />}
      {openDiscussion && (
        <Box
          sx={
            isLessThanSm
              ? discussionInterfaceContainerMobile
              : { ...discussionInterfaceContainer }
          }
        >
          <Box
            id="header"
            sx={
              isLessThanSm
                ? discussionHeaderContainerMobile
                : discussionHeaderContainer
            }
          >
            <Box sx={{ display: "flex", gap: 1, flex: 7, paddingLeft: "15px" }}>
              <Box id="imageContainer" sx={IconContainer}>
                <img
                  src={Isabelle}
                  alt="isabel"
                  unselectable="on"
                  style={IconImageDesign}
                  onContextMenu={(e) => e.preventDefault()}
                />
              </Box>
              <Box
                id="chatBotNameAndDescription"
                sx={{ ...ChatBotTextCOntainer }}
              >
                <Typography sx={{ ...ChatPrimaryText, marginBottom: "-5px" }}>
                  Isabel
                </Typography>
                <Typography sx={ChatSecondaryText}>
                  {t("Votre assistante virtuelle")}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ ...closeButtonDesign, flex: 1 }}>
              <Box
                onClick={handleClick}
                sx={{ cursor: "pointer", color: "white" }}
              >
                <CloseIcon />
              </Box>
            </Box>
          </Box>
          <Box
            id="ChatBotMessagingbody"
            ref={chatEndRef}
            sx={{ ...MessagesContainerDesign }}
          >
            {messages.map((message, index) => {
              // Boxes are disabled (not clickable) in description and confirmation steps
              const boxesAreDisabled = claimStep === "description" || claimStep === "confirmation";

              // Show category boxes in all steps during claim submission
              const showCategoryBoxes = isClaimSubmissionMode &&
                (claimStep === "categories" || claimStep === "sites" || claimStep === "parkings" || claimStep === "description" || claimStep === "confirmation") &&
                message.data?.type === "categorySelection";

              // Show site boxes when in sites step or later
              const showSiteBoxes = isClaimSubmissionMode &&
                (claimStep === "sites" || claimStep === "parkings" || claimStep === "description" || claimStep === "confirmation") &&
                message.data?.type === "siteSelection";

              // Show parking boxes when in parkings step or later
              const showParkingBoxes = isClaimSubmissionMode &&
                (claimStep === "parkings" || claimStep === "description" || claimStep === "confirmation") &&
                message.data?.type === "parkingSelection";

              return (
                <Box key={index} sx={{ width: "93%" }}>
                  <CustomMessages index={index} message={message} />

                  {/* Render category boxes right after the category selection message */}
                  {showCategoryBoxes && (
                    <Box sx={{ marginTop: "10px", marginBottom: "10px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {ticketCategories.map((category) => (
                        <Box
                          key={category.id}
                          onClick={boxesAreDisabled ? undefined : () => handleSelectCategory(category)}
                          sx={{
                            px: "14px",
                            py: "8px",
                            border: "1px solid",
                            borderColor: selectedCategory?.id === category.id ? "#1B374C" : "#d0d0d0",
                            borderRadius: "4px",
                            cursor: boxesAreDisabled ? "not-allowed" : "pointer",
                            transition: "all 0.2s ease",
                            backgroundColor: selectedCategory?.id === category.id ? "#1B374C" : "white",
                            color: boxesAreDisabled ? "#999" : (selectedCategory?.id === category.id ? "white" : "#757575"),
                            fontSize: "13px",
                            fontWeight: "500",
                            opacity: boxesAreDisabled ? 0.6 : 1,
                            "&:hover": boxesAreDisabled ? {} : {
                              borderColor: "#1B374C",
                              color: selectedCategory?.id === category.id ? "white" : "#1B374C",
                              backgroundColor: selectedCategory?.id === category.id ? "#152b3c" : "#f5f5f5",
                            },
                          }}
                        >
                          {getLocalizedName(category.name)}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Render site boxes right after the site selection message */}
                  {showSiteBoxes && (
                    <Box sx={{ marginTop: "10px", marginBottom: "10px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {parkingSites.map((site) => (
                        <Box
                          key={site.id}
                          onClick={boxesAreDisabled ? undefined : () => handleSelectSite(site)}
                          sx={{
                            px: "14px",
                            py: "8px",
                            border: "1px solid",
                            borderColor: selectedSite?.id === site.id ? "#1B374C" : "#d0d0d0",
                            borderRadius: "4px",
                            cursor: boxesAreDisabled ? "not-allowed" : "pointer",
                            transition: "all 0.2s ease",
                            backgroundColor: selectedSite?.id === site.id ? "#1B374C" : "white",
                            color: boxesAreDisabled ? "#999" : (selectedSite?.id === site.id ? "white" : "#757575"),
                            fontSize: "13px",
                            fontWeight: "500",
                            opacity: boxesAreDisabled ? 0.6 : 1,
                            "&:hover": boxesAreDisabled ? {} : {
                              borderColor: "#1B374C",
                              color: selectedSite?.id === site.id ? "white" : "#1B374C",
                              backgroundColor: selectedSite?.id === site.id ? "#152b3c" : "#f5f5f5",
                            },
                          }}
                        >
                          {getLocalizedName(site.siteName)}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Render parking boxes right after the parking selection message */}
                  {showParkingBoxes && selectedSite && (
                    <Box sx={{ marginTop: "10px", marginBottom: "10px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {selectedSite.parkings.map((parking) => (
                        <Box
                          key={parking.id}
                          onClick={boxesAreDisabled ? undefined : () => handleSelectParking(parking)}
                          sx={{
                            px: "14px",
                            py: "8px",
                            border: "1px solid",
                            borderColor: selectedParking?.id === parking.id ? "#1B374C" : "#d0d0d0",
                            borderRadius: "4px",
                            cursor: boxesAreDisabled ? "not-allowed" : "pointer",
                            transition: "all 0.2s ease",
                            backgroundColor: selectedParking?.id === parking.id ? "#1B374C" : "white",
                            color: boxesAreDisabled ? "#999" : (selectedParking?.id === parking.id ? "white" : "#757575"),
                            fontSize: "13px",
                            fontWeight: "500",
                            opacity: boxesAreDisabled ? 0.6 : 1,
                            "&:hover": boxesAreDisabled ? {} : {
                              borderColor: "#1B374C",
                              color: selectedParking?.id === parking.id ? "white" : "#1B374C",
                              backgroundColor: selectedParking?.id === parking.id ? "#152b3c" : "#f5f5f5",
                            },
                          }}
                        >
                          {getLocalizedName(parking.parkingName)}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
            {pending && openDiscussion && (
              <Box sx={typingInnerMessageContainer}>
                <TypingMessage />
              </Box>
            )}
            {!pending &&
              openDiscussion &&
              displayFAQ &&
              messages.length === 1 &&
              isFirstInteraction === "1" && (
                <Box sx={{ width: "93%" }}>
                  <Suggestions
                    askedQuestions={fAskedQuestions}
                    handleClick={handleClickMessage}
                  />
                </Box>
              )}
          </Box>
          {/* Footer */}
          <Box id="footer" sx={{ ...sendMessageContainer, display: showFooter ? "flex" : "none" }}>
            {showConfirmationButtons ? (
              /* Confirmation buttons */
              /* Confirmation buttons */
              <Box sx={{ display: "flex", width: "100%", gap: "10px", padding: "0 10px" }}>
                <Button
                  variant="outlined"
                  disabled={pending}
                  onClick={handleCancelClaim}
                  sx={{
                    flex: 1,
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1B374C",
                    borderColor: "#1B374C",
                    textTransform: "none",
                    borderRadius: "8px",
                    "&:hover": {
                      borderColor: "#1B374C",
                      backgroundColor: "rgba(27, 55, 76, 0.08)",
                    },
                  }}
                >
                  {t("Annuler")}
                </Button>
                <Button
                  variant="contained"
                  disabled={pending}
                  onClick={handleSendReclamation}
                  sx={{
                    flex: 1,
                    fontSize: "14px",
                    fontWeight: "600",
                    backgroundColor: "#da8d30",
                    color: "white",
                    boxShadow: "none",
                    textTransform: "none",
                    borderRadius: "8px",
                    "&:hover": {
                      backgroundColor: "#c67d25",
                      boxShadow: "none",
                    },
                  }}
                >
                  {t("Confirmer")}
                </Button>
              </Box>
            ) : (
              /* Normal chat input */
              <>
                <Box sx={{ flex: 6 }}>
                  <TextField
                    id="outlined-textarea"
                    placeholder={isClaimSubmissionMode && claimStep === "description" ? t("Décrivez votre réclamation...") : "Aa..."}
                    multiline
                    fullWidth
                    value={isClaimSubmissionMode && claimStep === "description" ? reclamationText : messageInput}
                    onChange={(e) => {
                      if (isClaimSubmissionMode && claimStep === "description") {
                        setReclamationText(e.target.value);
                      } else {
                        setMessageInput(e.target.value);
                      }
                    }}
                    maxRows={3}
                    onKeyDown={handleKeyDown}
                    InputProps={{ style: sendMessageInputDesign }}
                  />
                </Box>
                <Button
                  sx={{ flex: 1, color: "#1B374C" }}
                  disabled={pending || (isClaimSubmissionMode && claimStep === "description" && reclamationText.trim() === "")}
                  onClick={() => {
                    if (isClaimSubmissionMode && claimStep === "description") {
                      handleSubmitDescription();
                    } else {
                      handleSendMessage(messageInput, "me");
                    }
                  }}
                >
                  <SendIcon />
                </Button>
              </>
            )}
          </Box>
        </Box>
      )}
      <LoginDialog
        handleCloseDialog={handleCloseLoginDialog}
        openDialog={openLoginDialog}
      />
    </Box>
  );
};

export default ChatBot;
